<template>
	<div class="cog-payment__order-details cog-col" :class="[colNumClass]">
		<table>
			<tbody v-if="showLineItems" class="cog-payment__line-items">
				<!-- https://forum.vuejs.org/t/v-for-on-templates/8359/2 -->
				<!-- https://www.w3.org/WAI/tutorials/tables/multi-level/ -->
				<template v-for="lineItem in order.LineItems">
					<tr v-if="renderGroup(lineItem.Group)" :key="getGroupKey(lineItem)">
						<th :id="getGroupId(lineItem.Group)" colspan="2">
							{{ lineItem.Group }}
						</th>
					</tr>
					<tr :key="getLineItemKey(lineItem)">
						<td :headers="lineItem.Group ? getGroupId(lineItem.Group) : null" :class="lineItem.Group ? 'cog-order-details__indented-line-item' : null">
							{{ lineItem.Name }}
							<span v-if="lineItem.Description" class="cog-label__description"> {{ lineItem.Description }}</span>
						</td>
						<td :headers="lineItem.Group ? getGroupId(lineItem.Group) : null">{{ $format(lineItem.Amount, "C") }}</td>
					</tr>
				</template>
			</tbody>
			<tbody v-if="renderSubtotal" class="cog-payment__subtotal">
				<tr>
					<td class="cog-right">{{ $resource("payment-subtotal") }}:</td>
					<td>{{ $format(order.SubTotal || 0, "C") }}</td>
				</tr>
			</tbody>
			<tbody v-if="order.Fees && filteredFees.length > 0" class="cog-fees">
				<tr v-for="fee in filteredFees" :key="getLineItemKey(fee)">
					<td class="cog-right">{{ fee.Name }}:</td>
					<td>{{ fee.Amount ? $format(fee.Amount, "C") : "—" }}</td>
				</tr>
			</tbody>
			<tbody v-if="readonly && order && order.PaymentAccountStatus == PaymentAccountStatus.Active && order.PaymentStatus != PaymentStatus.Pending && order.AmountDue > 0">
				<tr>
					<td class="cog-right">{{ $resource("order-amount-paid") }}:</td>
					<td>{{ '(' + $format(order.AmountPaid, "C") + ')' }}</td>
				</tr>
				<tr>
					<td class="cog-right">{{ $resource('order-balance-due-label') }}:</td>
					<td>{{ $format(order.AmountDue, "C") }}</td>
				</tr>
			</tbody>
			<tfoot>
				<tr>
					<td colspan="2">
						<div class="cog-order-details__total">
							{{ orderAmountLabel || "Order Amount" }}: {{ $format(includeFees ? (order.OrderAmount || 0) : (order.OrderAmount - order.ProcessingFees || 0), "C") }}
						</div>
					</td>
				</tr>
			</tfoot>
		</table>
	</div>
</template>

<script>
	import '../../style/_table.scss';
	import Col from '../Col.vue';
	import { PaymentAccountStatus } from '@cognitoforms/types/server-types/payment/model/payment-account-status';
	import { PaymentStatus } from '@cognitoforms/types/server-types/payment/model/payment-status';

	export default {
		name: 'COrderDetails',
		mixins: [Col],
		inject: ['$resource', '$format'],
		props: {
			order: { type: Object, required: true },
			showLineItems: { type: Boolean, default: true },
			showSubTotal: { type: Boolean, default: true },
			orderAmountLabel: { type: String, default: null },
			readonly: { type: Boolean, default: false },
			includeFees: { type: Boolean, default: true }
		},
		data() {
			return {
				PaymentAccountStatus: PaymentAccountStatus,
				PaymentStatus: PaymentStatus
			};
		},
		computed: {
			renderSubtotal() {
				return this.showSubTotal || this.order.Fees.length;
			},
			filteredFees() {
				// We don't want processing fees if the user marks as paid (includeFees false and the item is a processing fee)
				return this.order.Fees.filter(item => this.includeFees || !item.IsProcessingFee);
			}
		},
		created() {
			// IMPORTANT: Making `lastRenderGroup`, `__getGroupKeyData`, or `getLineItemKeyData` data (reactive) will cause the component to re-render infinitely
			this.__lastRenderedGroup = null;
			this.__getLineItemKeyData = [];
			this.__getGroupKeyData = [];
		},
		beforeUpdate() {
			this.__lastRenderedGroup = null;
			this.__getLineItemKeyData = [];
			this.__getGroupKeyData = [];
		},
		methods: {
			renderGroup(groupId) {
				if (!groupId)
					return false;
				if (this.__lastRenderedGroup === groupId)
					return false;
				this.__lastRenderedGroup = groupId;
				return true;
			},
			getLineItemKey(lineItem) {
				let key = (lineItem.Group || '') + lineItem.Name + (lineItem.Description || '');
				if (this.__getLineItemKeyData.includes(key)) {
					let i = 1;
					while (this.__getLineItemKeyData.includes(key + i))
						i++;
					key += i;
				}
				this.__getLineItemKeyData.push(key);
				return key;
			},
			getGroupKey(lineItem) {
				let key = lineItem.Group;
				if (this.__getGroupKeyData.includes(key)) {
					let i = 1;
					while (this.__getGroupKeyData.includes(key + i))
						i++;
					key += i;
				}
				this.__getGroupKeyData.push(key);
				return key;
			},
			getGroupId(groupName) {
				return 'cog-group-' + groupName.replace(/ /g, '-').toLowerCase();
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-payment__order-details {

		table {
			border-collapse: collapse;
		}

		tbody {

			td,
			th {
				padding: $gutter-quarter 0;
				word-break: break-word;
				word-wrap: break-word;
			}

			td:last-child {
				padding-left: .5em;
				text-align: right;
				white-space: nowrap;
			}
		}
	}

	tbody td.cog-order-details__indented-line-item {
		padding-top: 0;
		padding-left: var(--gutter);
	}

	.cog-payment__line-items {

		th {
			font-size: .875em;
			text-align: left;
			text-transform: uppercase;
		}

		th,
		td {
			word-break: break-word;
			word-wrap: break-word;
		}
	}

	.cog-order-details__total {
		border-top: calc(var(--border-width) * 1.5) solid hsla(var(--background-hsl), 1);
		padding-top: $gutter-quarter;
		font-size: 1.219em;
		font-weight: bold;
		text-align: right;
	}

	.cog-payment__subtotal {
		font-weight: bold;
	}

	.cog-lineitem-group {
		font-weight: bold;
	}

	td.cog-lineitem-label.cog-grouped-line-item {
		padding-left: 20px;
	}
}
</style>